import {
  DynamoDbLocationDetail,
  DynamoDbFODwellingRisk,
  DynamoDbFOPropertyStructuresRisk,
  DynamoDbFarmRiskDetail
} from '@ourbranch/schema-types/dynamodb';
import { FarmAddressDetails } from '@ourbranch/schema-types/staff-appsync';
import { FarmRiskType } from '@ourbranch/lookups';

export const DWELLING_RISK_TYPES = [FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3];

/**
 * Flattens all risks from farm locations into a single array
 * @param farmLocations Array of farm locations
 * @returns Array of all risks from all locations, filtered for null/undefined values
 */
export function flattenFarmRisks(
  farmLocations: (DynamoDbLocationDetail | null | undefined)[]
): DynamoDbFarmRiskDetail[] {
  if (!farmLocations || !Array.isArray(farmLocations)) {
    return [];
  }

  return farmLocations
    .filter((location): location is DynamoDbLocationDetail => location !== null)
    .flatMap((location) => (location.risks?.filter(Boolean) || []) as DynamoDbFarmRiskDetail[]);
}

/**
 * Filters risks to only include dwelling risks (FO1, FO2, FO3)
 * @param risks Array of farm risks
 * @returns Array of dwelling risks only
 */
export function filterDwellingRisks(risks: DynamoDbFarmRiskDetail[]): DynamoDbFODwellingRisk[] {
  return risks.filter(
    (risk): risk is DynamoDbFODwellingRisk =>
      !!risk &&
      !!risk.type &&
      (risk.type === FarmRiskType.Fo1 ||
        risk.type === FarmRiskType.Fo2 ||
        risk.type === FarmRiskType.Fo3 ||
        DWELLING_RISK_TYPES.includes(risk.type.toUpperCase()))
  );
}

/**
 * Filters risks to only include property structures risks (FO6)
 * @param risks Array of farm risks
 * @returns Array of property structures risks only
 */
export function filterPropertyStructureRisks(risks: DynamoDbFarmRiskDetail[]): DynamoDbFOPropertyStructuresRisk[] {
  return risks.filter(
    (risk): risk is DynamoDbFOPropertyStructuresRisk =>
      !!risk && !!risk.type && risk.type === FarmRiskType.Fo6Structures && 'coverages' in risk
  );
}

/**
 * Gets dwelling risks from farm locations in a single operation
 * @param farmLocations Array of farm locations
 * @returns Array of dwelling risks only
 */
export function getDwellingRisksFromLocations(
  farmLocations: (DynamoDbLocationDetail | null | undefined)[]
): DynamoDbFODwellingRisk[] {
  const allRisks = flattenFarmRisks(farmLocations as DynamoDbLocationDetail[]);
  return filterDwellingRisks(allRisks);
}

/**
 * Gets property structures risks from farm locations in a single operation
 * @param farmLocations Array of farm locations
 * @returns Array of property structures risks only
 */
export function getPropertyStructureRisksFromLocations(
  farmLocations: (DynamoDbLocationDetail | null | undefined)[]
): DynamoDbFOPropertyStructuresRisk[] {
  const allRisks = flattenFarmRisks(farmLocations as DynamoDbLocationDetail[]);
  return filterPropertyStructureRisks(allRisks);
}

/**
 * Checks if a risk is a dwelling risk type
 * @param riskType The risk type to check
 * @returns True if the risk is a dwelling type (FO1, FO2, or FO3)
 */
export function isDwellingRiskType(riskType: string): boolean {
  if (!riskType) {
    return false;
  }

  const upperType = riskType.toUpperCase();
  return (
    upperType === FarmRiskType.Fo1 ||
    upperType === FarmRiskType.Fo2 ||
    upperType === FarmRiskType.Fo3 ||
    DWELLING_RISK_TYPES.includes(upperType)
  );
}

/**
 * Checks if a risk is a renters risk type
 * @param riskType The risk type to check
 * @returns True if the risk is a renters type (FO4)
 */
export function isRentersRiskType(riskType: string): boolean {
  if (!riskType) {
    return false;
  }

  const upperType = riskType.toUpperCase();
  return upperType === FarmRiskType.Fo4;
}

/**
 * Checks if a risk is a property risk type
 * @param riskType The risk type to check
 * @returns True if the risk is a property type (FO6)
 */
export function isPropertyRiskType(riskType: string): boolean {
  if (!riskType) {
    return false;
  }

  const upperType = riskType.toUpperCase();
  return (
    upperType === FarmRiskType.Fo6Structures.toUpperCase() ||
    upperType === FarmRiskType.Fo6Scheduled.toUpperCase() ||
    upperType === FarmRiskType.Fo6Unscheduled.toUpperCase()
  );
}

export interface FarmAddressEntity {
  id: string;
  type: string;
  address: FarmAddressDetails;
}

/**
 * Extracts all unique addresses from farm locations
 * @param farmLocations Array of farm locations
 * @returns Array of address entities with id, type, and address, deduplicated by address
 */
export function getAllFarmAddressEntities(
  farmLocations?: (DynamoDbLocationDetail | null | undefined)[]
): FarmAddressEntity[] {
  const entities: FarmAddressEntity[] = [];

  if (!farmLocations || !Array.isArray(farmLocations)) {
    return entities;
  }

  farmLocations.forEach((location) => {
    if (!location) {
      return;
    }

    // Location address only - risks now use their parent location's address
    if (location.address) {
      entities.push({
        id: location.id,
        type: 'location',
        address: location.address
      });
    }
  });

  // Deduplicate by address (simple string comparison)
  const seen = new Set<string>();
  return entities.filter((entity) => {
    const addressKey = `${entity.address.address}_${entity.address.city}_${entity.address.state}_${entity.address.zip}`;
    if (seen.has(addressKey)) {
      return false;
    }
    seen.add(addressKey);
    return true;
  });
}

/**
 * Custom error for farm state validation failures
 */
export class FarmStateError extends Error {
  constructor(
    message: string,
    public readonly farmLocations?: any[]
  ) {
    super(message);
    this.name = 'FarmStateError';
  }
}

/**
 * Gets the primary state for farm operations - STRICT VERSION
 * Throws error if primary farm location doesn't have a state
 * For farm insurance, state MUST come from primary location only
 * @param farmLocations Array of farm locations
 * @returns Primary farm location state
 * @throws {FarmStateError} When farm location data is invalid or missing state
 */
export function getFarmPrimaryStateStrict(farmLocations?: any[]): string {
  if (!farmLocations || !Array.isArray(farmLocations) || farmLocations.length === 0) {
    throw new FarmStateError('No farm locations provided for state determination');
  }

  const primaryLocation = farmLocations[0];
  if (!primaryLocation?.address) {
    throw new FarmStateError('Primary farm location has no address', farmLocations);
  }

  const primaryState = primaryLocation.address.state;
  if (!primaryState || primaryState.trim() === '') {
    throw new FarmStateError('Primary farm location has no state', farmLocations);
  }

  return primaryState.trim();
}

/**
 * Determines if a quote/policy is a farm policy based on farmLocations presence
 * @param quote The quote or policy object
 * @returns True if the object has farmLocations with at least one location
 */
export function isFarmPolicy(quote: any): boolean {
  return quote?.farmLocations && Array.isArray(quote.farmLocations) && quote.farmLocations.length > 0;
}

/**
 * Normalizes county names by removing " COUNTY" suffix and converting to uppercase
 * Used for consistent county name formatting across mine subsidence defaults and other county-based lookups
 * @param county The county name to normalize (e.g., "Bond County" or "BOND COUNTY")
 * @returns Normalized county name in uppercase without suffix (e.g., "BOND"), or empty string if null/undefined
 */
export function normalizeCountyName(county: string | null | undefined): string {
  return county?.replace(/ COUNTY/i, '').toUpperCase() ?? '';
}

/**
 * Occupancy codes for Silos/Grain Bins (SG building type) that use Length × Width
 * dimensions instead of Diameter for sqFt calculation and Verisk 360Value submissions.
 * CC = Corn Crib, GS = Commodity Shed, GH = Grain/Feed Handling Center
 * GitHub Issue: #29355
 */
export const LENGTH_WIDTH_OCCUPANCIES: readonly string[] = ['CC', 'GS', 'GH'];

/**
 * Checks if an occupancy type uses length and width instead of diameter.
 * @param occupancy - The occupancy code
 * @returns True if occupancy uses length/width dimensions
 */
export function usesLengthWidth(occupancy: string | null | undefined): boolean {
  if (!occupancy) {
    return false;
  }
  return LENGTH_WIDTH_OCCUPANCIES.includes(occupancy);
}

/**
 * Resolves a deductible value to a dollar amount.
 *
 * Forreston brand in Illinois stores deductibles as either percentage values (< 1, e.g. 0.01 = 1%)
 * or dollar amounts (>= 1). This function normalises both forms to a dollar amount so that the
 * backend rater and the frontend useEffect use identical resolution logic and never drift apart.
 *
 * @param deductibleValue - The raw deductible value (percentage < 1 or dollar amount >= 1)
 * @param coverageLimit - The coverage limit used to calculate the dollar amount for percentage values
 * @returns The resolved dollar amount, or 0 when inputs are missing
 */
export function resolveDeductibleAmount(deductibleValue: number | null | undefined, coverageLimit: number): number {
  // No deductible value → return 0
  if (deductibleValue == null) {
    return 0;
  }

  // No coverage limit → can't resolve a percentage, return original value
  if (!coverageLimit) {
    return deductibleValue;
  }

  // Values < 1 are percentages (e.g., 0.01 = 1%); >= 1 are already dollar amounts
  if (deductibleValue < 1) {
    return Math.round(coverageLimit * deductibleValue);
  }

  return deductibleValue;
}

// Re-export farm policy utilities
export { makePolicyDetailsFromSegments } from './farm-policy-utils';
